/**
 * Public `/ai-safety` page — full transparency about every AI clinical
 * surface on the platform. Linked from every `<AISafetyBadge/>` modal.
 *
 * What it does (and why):
 *   - Lists every AI surface from `data/ai_surfaces.js`
 *   - Shows risk class, inputs, outputs, viewer, active guardrails
 *   - Shows validation state per surface (signed | refresh due | pending)
 *   - Documents the BAA chain (Vertex AI / Whisper) for HIPAA-covered
 *     surfaces
 *   - Honest by default — pending surfaces are clearly labeled, not hidden
 *
 * Clinicians actively look for this page before adopting AI-powered
 * healthcare platforms. Hiding the AI surfaces (or putting them behind
 * marketing fluff) damages trust more than disclosing them transparently.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, ShieldCheck, BadgeCheck, AlertTriangle, ChevronLeft, Lock, FileText } from 'lucide-react';
import { AI_SURFACES } from '../data/ai_surfaces';

const RISK_PILL = {
  high:   'bg-rose-50 text-rose-700 border-rose-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-cn-bone-100 text-cn-text-muted border-cn-border',
};

function validationState(surface) {
  const v = surface.clinical_validation;
  if (!v) return { kind: 'pending', label: 'Validation pending', cls: 'bg-cn-bone-100 text-cn-text-muted border-cn-border', Icon: Brain };
  const expired = v.expires_at ? new Date(v.expires_at) < new Date() : false;
  if (expired) return { kind: 'expired', label: 'Refresh due', cls: 'bg-amber-100 text-amber-700 border-amber-300', Icon: AlertTriangle };
  return { kind: 'signed', label: 'Clinician-validated', cls: 'bg-sky-100 text-sky-700 border-sky-300', Icon: BadgeCheck };
}

export default function AISafetyPage() {
  const counts = AI_SURFACES.reduce((acc, s) => {
    const k = validationState(s).kind;
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-cn-bone dark:bg-cn-ink-950" data-testid="ai-safety-page">
      <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-cn-text-muted hover:text-cn-primary mb-6">
          <ChevronLeft className="w-4 h-4" /> Back to CaraLoom
        </Link>

        <div className="inline-flex w-14 h-14 rounded-2xl bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 items-center justify-center mb-4">
          <Brain className="w-6 h-6" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-cn-text dark:text-white leading-tight">
          AI safety, in writing.
        </h1>
        <p className="text-base sm:text-lg text-cn-text-muted mt-4 max-w-3xl">
          Every AI surface on the CaraLoom platform is listed here — what it
          does, what data it sees, who reviews its output, and the active
          guardrails in place. We refresh each clinician-validated badge
          every 6 months. No marketing language, no hidden surfaces.
        </p>

        {/* At-a-glance counts */}
        <div className="grid sm:grid-cols-4 gap-4 mt-8">
          <div className="cn-card p-4" data-testid="ai-safety-count-total">
            <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Total AI surfaces</div>
            <div className="font-display text-3xl font-bold text-cn-text dark:text-white mt-1">{AI_SURFACES.length}</div>
          </div>
          <div className="cn-card p-4 border-sky-200/60" data-testid="ai-safety-count-signed">
            <div className="text-[11px] uppercase tracking-wider font-bold text-sky-700 dark:text-sky-300">Clinician-validated</div>
            <div className="font-display text-3xl font-bold text-sky-700 dark:text-sky-300 mt-1">{counts.signed || 0}</div>
          </div>
          <div className="cn-card p-4 border-amber-200/60" data-testid="ai-safety-count-expired">
            <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300">Refresh due</div>
            <div className="font-display text-3xl font-bold text-amber-700 dark:text-amber-300 mt-1">{counts.expired || 0}</div>
          </div>
          <div className="cn-card p-4" data-testid="ai-safety-count-pending">
            <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Validation pending</div>
            <div className="font-display text-3xl font-bold text-cn-text dark:text-white mt-1">{counts.pending || 0}</div>
          </div>
        </div>

        {/* Platform-wide guardrails */}
        <div className="mt-12">
          <h2 className="font-display text-2xl font-bold text-cn-text dark:text-white">Platform-wide guardrails</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div className="cn-card p-5 border-emerald-200/60">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-emerald-700" />
                <div className="font-bold text-cn-text dark:text-white">BAA-covered LLM chain</div>
              </div>
              <p className="text-sm text-cn-text-muted leading-snug">
                All PHI-touching AI calls route through Google Vertex AI under
                an active BAA. A runtime guard in <code className="text-[11px] bg-cn-bone-100 dark:bg-cn-ink-800 px-1 rounded">_ai_send_phi</code>{' '}
                blocks any OpenAI fallback. Whisper STT is wrapped in the same
                BAA chain for voice-to-SOAP.
              </p>
            </div>
            <div className="cn-card p-5 border-emerald-200/60">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                <div className="font-bold text-cn-text dark:text-white">PHI scrub before LLM boundary</div>
              </div>
              <p className="text-sm text-cn-text-muted leading-snug">
                Identifying fields (names, addresses, contact, IDs) are
                scrubbed at prompt-interpolation. Clinical values (vitals,
                diagnoses, scores) are preserved so the AI has the signal it
                needs without seeing identifiers.
              </p>
            </div>
            <div className="cn-card p-5 border-emerald-200/60">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-emerald-700" />
                <div className="font-bold text-cn-text dark:text-white">Centralized PHI audit log</div>
              </div>
              <p className="text-sm text-cn-text-muted leading-snug">
                Every AI call against patient data emits an audit-log entry
                — caller, patient, surface, timestamp, request id — into the
                tamper-evident audit collection. Admins can produce a full
                chain-of-custody for any audit.
              </p>
            </div>
            <div className="cn-card p-5 border-emerald-200/60">
              <div className="flex items-center gap-2 mb-2">
                <BadgeCheck className="w-4 h-4 text-emerald-700" />
                <div className="font-bold text-cn-text dark:text-white">Clinician-in-the-loop</div>
              </div>
              <p className="text-sm text-cn-text-muted leading-snug">
                AI never autonomously charts, prescribes, submits a claim,
                or escalates care. A licensed clinician signs every final
                clinical record. AI outputs are drafts and advisory signals,
                not autonomous actions.
              </p>
            </div>
          </div>
        </div>

        {/* Per-surface registry */}
        <div className="mt-12">
          <h2 className="font-display text-2xl font-bold text-cn-text dark:text-white">Every AI surface, in detail</h2>
          <p className="text-sm text-cn-text-muted mt-1">
            {AI_SURFACES.length} surfaces total. Each runs under the platform-wide guardrails above plus the per-surface guardrails listed below.
          </p>

          <div className="space-y-5 mt-6">
            {AI_SURFACES.map((s) => {
              const state = validationState(s);
              const StateIcon = state.Icon;
              return (
                <div key={s.id} className="cn-card p-6" data-testid={`ai-safety-surface-${s.id}`}>
                  <div className="flex flex-wrap items-start gap-3 justify-between">
                    <div>
                      <div className="font-display text-lg font-bold text-cn-text dark:text-white">{s.name}</div>
                      <div className="text-sm text-cn-text-muted">{s.surface_label}</div>
                      <code className="text-[11px] text-cn-text-muted block mt-1">{s.endpoint}</code>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${RISK_PILL[s.risk_class]}`}>
                        {s.risk_class}-risk
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full border ${state.cls}`}>
                        <StateIcon className="w-3 h-3" /> {state.label}
                      </span>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4 mt-5">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Inputs</div>
                      <ul className="text-xs text-cn-text dark:text-cn-bone-50 mt-1 space-y-0.5">
                        {s.inputs.map((i, idx) => <li key={idx}>· {i}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Outputs</div>
                      <ul className="text-xs text-cn-text dark:text-cn-bone-50 mt-1 space-y-0.5">
                        {s.outputs.map((o, idx) => <li key={idx}>· {o}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Viewer</div>
                      <div className="text-xs text-cn-text dark:text-cn-bone-50 mt-1">{s.viewer}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted mt-3">Per-surface guardrails</div>
                      <ul className="text-xs text-cn-text dark:text-cn-bone-50 mt-1 space-y-0.5">
                        {s.guardrails.map((g, idx) => <li key={idx}>· {g}</li>)}
                      </ul>
                    </div>
                  </div>

                  {s.clinical_validation && (
                    <div className="mt-5 pt-4 border-t border-cn-border text-xs text-cn-text-muted">
                      Validated by <strong className="text-cn-text dark:text-white">{s.clinical_validation.reviewer_name}</strong>
                      {' · '}NPI {s.clinical_validation.npi}
                      {' · '}{s.clinical_validation.cases_reviewed} cases
                      {' · '}{Math.round((s.clinical_validation.pass_rate || 0) * 100)}% pass-rate
                      {' · refresh due '}{new Date(s.clinical_validation.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-xs text-cn-text-muted mt-10 leading-snug max-w-3xl">
          Methodology: each clinician-validated badge is signed by a licensed
          clinician (MD, DNP, NP, or RN with relevant specialty) after a
          standardized review of golden-set test cases (typically 24–40
          per surface). Pass-rate is the fraction of cases scored ≥3/5 on the
          per-surface rubric. Refreshes are scheduled every 6 months.
          See <code className="text-[11px] bg-cn-bone-100 dark:bg-cn-ink-800 px-1 rounded">/app/memory/CLINICAL_VALIDATION_BRIEF.md</code>{' '}
          for the rubric and golden cases.
        </p>
      </div>
    </div>
  );
}
